import React, { useState, useEffect, createContext } from 'react';
import { RefreshProvider } from './RefreshContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import './App.css';
import LoginScreen from './components/LoginScreen';
import MenuPage from './components/MenuPage';
import ServerScreen from './components/ServerScreen';
import LanguageToggle from './components/LanguageToggle';
import DebugInfo from './components/DebugInfo';
import bocaLogo from './boca_logo.svg';
import { lightTheme, darkTheme } from './theme';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { useActivityTracker } from './hooks/useActivityTracker';
import { loadSystemFeatures } from './utils/systemFeatures';
import { BACKEND_URL, isDevTenant } from './config';

export const OptionsContext = createContext([]);

export function OptionsProvider({ children }) {
    const [allOptions, setAllOptions] = useState([]);
    useEffect(() => {
        fetch('/api/debug/options')
            .then(res => res.json())
            .then(data => setAllOptions(data))
            .catch(() => setAllOptions([]));
    }, []);
    return (
        <OptionsContext.Provider value={allOptions}>
            {children}
        </OptionsContext.Provider>
    );
}

function AppContent({ debugVisible, setDebugVisible }) {
	const [user, setUser] = useState(null);
	const [themeMode, setThemeMode] = useState('dark'); // 'dark' or 'light'
	const [customTheme, setCustomTheme] = useState(null); // For custom themes
	const [defaultLight, setDefaultLight] = useState(lightTheme);
	const [defaultDark, setDefaultDark] = useState(darkTheme);
	const [logoSrc, setLogoSrc] = useState(`${BACKEND_URL}/api/branding/logo`);
	const theme = customTheme || (themeMode === 'dark' ? defaultDark : defaultLight);
	const navigate = useNavigate();
	const { t } = useLanguage();
	const timeRef = React.useRef(null);
	const [debugTapCount, setDebugTapCount] = useState(0);
	const [debugTapTimeout, setDebugTapTimeout] = useState(null);

	// Fetch branding (default themes + default mode + refresh logo)
	const fetchBranding = React.useCallback(() => {
		fetch(`${BACKEND_URL}/api/branding`)
			.then(res => res.json())
			.then(data => {
				if (data.defaultLightTheme) setDefaultLight(data.defaultLightTheme);
				if (data.defaultDarkTheme) setDefaultDark(data.defaultDarkTheme);
				if (data.defaultThemeMode) setThemeMode(data.defaultThemeMode);
				setCustomTheme(null); // Use updated defaults
				// Refresh logo with cache-bust (handles upload/delete)
				setLogoSrc(`${BACKEND_URL}/api/branding/logo?t=${Date.now()}`);
			})
			.catch(() => {
				// Keep hardcoded fallbacks on error
			});
	}, []);

	useEffect(() => {
		fetchBranding();
		// Warm the feature settings cache the receipt and ticket builders read
		loadSystemFeatures();
	}, []);

	// Update current time every second without causing re-renders
	useEffect(() => {
		const updateTime = () => {
			if (timeRef.current) {
				timeRef.current.textContent = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
			}
		};
		updateTime(); // Initial update
		const interval = setInterval(updateTime, 1000);
		return () => clearInterval(interval);
	}, []);

	const handleToggleTheme = () => {
		setThemeMode(prev => (prev === 'dark' ? 'light' : 'dark'));
		setCustomTheme(null); // Clear custom theme when toggling
	};

	const handleThemeChange = (newTheme) => {
		setCustomTheme(newTheme);
	};

	// Handle Gibbons logo click - single click navigates, 5 taps toggle debug
	const handleGibbonsClick = () => {
		// Clear existing timeout
		if (debugTapTimeout) clearTimeout(debugTapTimeout);

		const newCount = debugTapCount + 1;
		setDebugTapCount(newCount);

		// If 5 taps, toggle debug
		if (newCount >= 5) {
			setDebugVisible(!debugVisible);
			setDebugTapCount(0);
		} else {
			// Single click - navigate to default screen
			window.dispatchEvent(new CustomEvent('navigateToDefaultScreen'));
		}

		// Reset tap count after 1 second
		const timeout = setTimeout(() => setDebugTapCount(0), 1000);
		setDebugTapTimeout(timeout);
	};

	// On successful login, navigate to /pos
	const handleLogin = (userData) => {
		setUser(userData);
		navigate('/pos');
	};

	// Force logout (no confirmation) - used by activity tracker and session checks
	const forceLogout = async () => {
		if (user?.id) {
			try {
				await fetch(`${BACKEND_URL}/api/session/${user.id}`, {
					method: 'DELETE'
				});
			} catch (error) {
				console.error('Failed to remove session:', error);
			}
		}
		setUser(null);
		navigate('/');
	};

	// User-initiated logout (with confirmation)
	const handleLogout = async () => {
		if (!window.confirm(t('confirmLogout'))) return;
		await forceLogout();
	};

	// Initialize activity tracker when user is logged in
	useActivityTracker(user?.id, forceLogout);

	// Sync body background color with theme
	useEffect(() => {
		document.body.style.backgroundColor = theme.colors.background;
		document.documentElement.style.backgroundColor = theme.colors.background;
	}, [theme.colors.background]);

	// Warn on browser refresh/tab close when user is logged in
	useEffect(() => {
		if (!user) return;
		const handleBeforeUnload = (e) => {
			e.preventDefault();
		};
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [user]);

	// Prevent browser back button from navigating away from /pos
	useEffect(() => {
		if (!user) return;
		window.history.pushState({ posApp: true }, '');
		const handlePopState = (event) => {
			if (!event.state || (!event.state.modal && !event.state.orderScreen)) {
				window.history.pushState({ posApp: true }, '');
			}
		};
		window.addEventListener('popstate', handlePopState);
		return () => window.removeEventListener('popstate', handlePopState);
	}, [user]);

	return (
		<RefreshProvider>
			<OptionsProvider>
				<PermissionsProvider userRole={user?.user_role}>
					<Routes>
						<Route
							path="/"
							element={<LoginScreen onLogin={handleLogin} theme={theme} themeMode={themeMode} onToggleTheme={handleToggleTheme} logoSrc={logoSrc} />}
						/>
						<Route
							path="/pos"
							element={
								user ? (
									<div
										className="pos-app"
										style={{
											background: theme.colors.background,
											color: theme.colors.text,
											fontFamily: theme.fonts.main,
											minHeight: '100vh',
										}}
									>
										<header
											className="pos-header"
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '1rem',
												background: theme.colors.surface,
												color: theme.colors.text,
												boxShadow: theme.boxShadow,
												padding: '1rem',
												borderBottom: `1px solid ${theme.colors.border}`,
												position: 'relative',
												flexDirection: 'column',
											}}
										>
											<div className="header-row" style={{ width: '100%', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
												<img
													src={logoSrc}
													alt="Boca POS"
													style={{
														height: '48px',
														width: 'auto',
														cursor: 'pointer'
													}}
													onClick={handleGibbonsClick}
													title={t('backToDashboard') || t('back')}
													onError={(e) => { e.target.onerror = null; e.target.src = bocaLogo; }}
												/>
												<div style={{ padding: '0rem 1rem', flex: '1 1 auto', textAlign: 'left', minWidth: '150px' }}>
													{t('user')}: {user.user_name} - {t('role')}: {t(user.role_title)}
												</div>
												<div className="header-clock" style={{ flex: '1 1 auto', textAlign: 'center', minWidth: '100px' }}>
													<span>{t('localTime')}: <span ref={timeRef}></span></span>
												</div>
												<div style={{ flex: '0 0 auto',
													display: 'flex',
													gap: '0.5rem',
													alignItems: 'center',
													padding: '0rem 0rem' }}>
													<LanguageToggle theme={theme} />
													<button
														onClick={handleLogout}
														style={{
															padding: '0.5rem .5rem',
															minWidth: '70px',
															height: '40px',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															borderRadius: theme.borderRadius,
															border: `1px solid ${theme.colors.border}`,
															background: theme.colors.secondary,
															color: theme.colors.textLight,
															fontWeight: 'bold',
															cursor: 'pointer',
														}}
													>
														{t('logout')}
													</button>
													<button
														onClick={handleToggleTheme}
														style={{
															padding: '0.5rem 0.5rem',
															minWidth: '48px',
															height: '40px',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															borderRadius: theme.borderRadius,
															border: `1px solid ${theme.colors.border}`,
															background: theme.colors.primary,
															color: theme.colors.textLight,
															fontWeight: 'bold',
															cursor: 'pointer',
															marginRight: '1.5rem',
														}}
													>
														{themeMode === 'dark' ? '🌞' : '🌙'}
													</button>

												</div>
											</div>

										</header>
										<main className="pos-main" style={{ padding: 0, margin: 0 }}>
											<ServerScreen user={user} theme={theme} onThemeChange={handleThemeChange} onBrandingChange={fetchBranding} />
										</main>
										<footer className="pos-footer"
											style={{ background: theme.colors.surface,
												color: theme.colors.text, textAlign: 'center',
												padding: '1rem',
												borderTop: `1px solid ${theme.colors.border}` }}>
											<small>&copy; 2026 BOCA POS</small>
										</footer>
									</div>
								) : (
									<Navigate to="/" />
								)
							}
						/>
						<Route path="/menu" element={<MenuPage />} />
						<Route path="/menu/:slug" element={<MenuPage />} />
						<Route path="*" element={<Navigate to={user ? "/pos" : "/"} />} />
					</Routes>
				</PermissionsProvider>
			</OptionsProvider>
		</RefreshProvider>
	);
}

function App() {
	// Auto-show debug panel only on the dev tenant
	// Can be overridden with ?debug=true or ?debug=false URL parameter
	const [debugVisible, setDebugVisible] = useState(isDevTenant());

	// Allow URL parameter override: ?debug=true or ?debug=false
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const debugParam = params.get('debug');
		if (debugParam === 'true') {
			setDebugVisible(true);
		} else if (debugParam === 'false') {
			setDebugVisible(false);
		}
	}, []);

	return (
		<LanguageProvider>
			<AppContent debugVisible={debugVisible} setDebugVisible={setDebugVisible} />
			<DebugInfo visible={debugVisible} setVisible={setDebugVisible} />
		</LanguageProvider>
	);
}

export default App;

// In OrderTotal.js, replace local allOptions and useEffect with:
// import { useContext } from 'react';
// import { OptionsContext } from '../App'; // or wherever you place it
// const allOptions = useContext(OptionsContext);
